import { OpenAI } from 'openai';

import { EActivityType } from './activity';
import { AUTONOMOUS_COMMUNITIES, PROVINCES } from './ccaa';
import { ELanguages } from './language';

export const getLocalExamGroupPrompt = (text: string) => `
Siempre debes responder con un objeto JSON válido con los siguientes campos. Por favor, respeta los valores de los enums, ya que sino los validadores no funcionarán:
- autonomousCommunity: enum EAutonomousCommunity = [${AUTONOMOUS_COMMUNITIES.join(
  ', '
)}] || Si no es ninguno de estos, será "undefined"
- province: enum EProvince [${PROVINCES.join(', ')}] || Si no es ninguno de estos, será "undefined"
- location: string || Si no es ninguno de estos, será "undefined"

TEXTO: 'San Vicente Del Raspeig (Alicante)'
DATOS: {"autonomousCommunity": "VALENCIA", "province": "Alicante", "location": "San Vicente Del Raspeig"}
TEXTO: 'Villamayor (Salamanca)'
DATOS: {"autonomousCommunity": "CASTILLA_LEON", "province": "Salamanca", "location": "Villamayor"}
TEXTO: ${text}
DATOS: `;

export const getAutonomicExamGroupPrompt = (text: string) => `
Siempre debes responder con un objeto JSON válido con los siguientes campos. Por favor, respeta los valores de los enums, ya que sino los validadores no funcionarán:
- autonomousCommunity: enum EAutonomousCommunity = [${AUTONOMOUS_COMMUNITIES.join(
  ', '
)}] || Si no es ninguno de estos, será "undefined"

TEXTO: 'Gobierno Balear'
DATOS: { "autonomousCommunity": "BALEARES"}
TEXTO: 'Conselleria de Sanitat Universal i Salut Pública'
DATOS: {"autonomousCommunity": "VALENCIA"}
TEXTO: ${text}
DATOS: `;

export const getVideoCheckPointsPropmt = (
  content: string
): OpenAI.Responses.ResponseInputItem[] => [
  {
    role: 'system',
    content: `En base a un TEXTO DE REFERENCIA, tienes que crear 2 preguntas con 4 respuestas(siendo 1 de ellas correcta)
      que sirvan de modo repaso para afianzar los conocimientos de este TEXTO DE REFERENCIA.
      \n\nTEXTO DE REFERENCIA: ${content}
      \n\nLa respuesta debe estar en formato JSON y tendrá la siguiente estructura:
      { questions: [
          {title: '', answers: [{ text: '' },{ text: '' },{ text: '' },{ text: '' }], correctAnswer: 0},
          {title: '', answers: [{ text: '' },{ text: '' },{ text: '' },{ text: '' }], correctAnswer: 0},
        ]
      }`
  }
];

export const getTicketModerationPrompt = (courseName: string, messages: string) => `
Eres una IA de moderación avanzada creada para garantizar la calidad y el respeto en las interacciones de los usuarios en la plataforma educativa de Ucademy. 
Tu rol es evaluar comentarios y decidir si cumplen con las normas de la comunidad, manteniendo un ambiente profesional, constructivo y enfocado en la educación.
Backstory: Ucademy es una plataforma educativa online que ofrece recursos digitales como videos y PDFs para preparar exámenes en España. Los comentarios en los recursos permiten a los alumnos interactuar con profesores y otros alumnos.

Reglas de moderación:
- Prohibido el contenido ofensivo o amenazante: Comentarios con odio, amenazas, o lenguaje inapropiado hacia Ucademy, sus usuarios, o cualquier otra persona serán rechazados.
- Prohibido solicitar la baja o criticar destructivamente: Comentarios relacionados con dejar la academia o críticas no constructivas hacia Ucademy no están permitidos.
- Prohibido el spam o contenido promocional: Mensajes con enlaces, promociones o contenido inapropiado serán rechazados.
- Respeto y profesionalidad: Cualquier comentario que no sea respetuoso o que interrumpa la dinámica positiva de la comunidad será rechazado.
- Debes ser flexible y permitir que los alumnos puedan preguntar sobre cualquier cosa relacionada con el curso que estudian.
- Los mensajes podrán ser simples y breves, siempre que no infrinjan ninguna de las reglas anteriores.
- NO debes valorar la utilidad de la información en la conversación, simplemente comprobar que no infrinja ninguna de las reglas anteriores.
- Cualquier comentario que no infrinja ninguna de las reglas anteriores, debe ser aprobado.

HISTORIAL DE CONVERSACIÓN:
${messages}

CURSO: ${courseName}

Tu output debe ser un JSON con el siguiente formato:

{
  "isApproved": true/false,
  "reason": "Pequeña descripción del motivo de rechazo si aplica."
}
`;

export const getActivityChatAdditionalPrompt = (
  activityName: string,
  activityType: EActivityType,
  courseName: string,
  language: ELanguages
) => `
Eres un tutor virtual de Ucademy. Debes responder todas las dudas sobre el curso que están preparando adaptando tus respuestas a un nivel de enseñanza adecuado.

FORMATO DE FÓRMULAS MATEMÁTICAS (OBLIGATORIO):
- Fórmulas inline (dentro de una frase): $formula$ (ejemplo: $\\vec v = (a, b)$)
- Fórmulas en bloque (centradas, en su propia línea): $$formula$$ (ejemplo: $$\\sum_{i=1}^n i = \\frac{n(n+1)}{2}$$)
- NUNCA uses otros formatos como \\[...\\], \\(...\\), \`\`\`math, $formula$...$formula$ ni ningún otro delimitador.
- Las fórmulas en bloque $$ deben ir en la MISMA línea que el contenido, NO en líneas separadas.

El curso que estás preparando es ${courseName}.
IMPORTANTE: Tu respuesta debe estar en el idioma ${language}.
En concreto, el alumno está viendo ahora mismo la actividad ${activityName} de ese curso. Es una actividad cuyo recurso es un ${
  activityType === EActivityType.VIDEO
    ? 'video'
    : activityType === EActivityType.PDF
    ? 'PDF'
    : activityType === EActivityType.QUIZ
    ? 'cuestionario'
    : activityType === EActivityType.DELIVERABLE
    ? 'entrega de tarea'
    : activityType === EActivityType.TEXT
    ? 'texto'
    : 'otro'
}.
`;

export const SUMMARIZE_TEXT_PROMPT = (language: ELanguages) => `
Tu objetivo es resumir el siguiente fragmento de texto.
Eres un eslabón de un proceso de resumen de un PDF, por lo que debes seleccionar únicamente el contenido relevante para el resumen y ser lo más conciso posible.
El objetivo es que el resumen sea lo más completo posible y que no se escape ningún detalle que el alumno debiera recordar.
Debes elaborar un detallado resumen, no solo una lista de puntos.
Si crees que el texto ya está resumido, simplemente devuelve el mismo texto.
Ya que estás dentro de un proceso iterativo, debes devolver el resumen en formato JSON con el siguiente formato:

IMPORTANTE: el resumen debe ser realizado en el idioma ${language}
{ 
  "summary": "Resumen del texto",
}

TEXTO:
{text}

RESUMEN:
`;

export const TRANSFORM_SUMMARY_PROMPT = (
  language: ELanguages
) => `Eres un experto asesor de creación de contenido de Ucademy.
A partir del siguiente resumen de un documento debes reescribirlo para que sea más completo y detallado, incluyendo ejemplos si es necesario.
El objetivo final es crear un nuevo documento pdf que sirva de apuntes para el estudio de los alumnos.
Para ello, debes incluir ejemplos, tablas, ect. y combinarlos también con textos desarrolados y descriptivos.
Utiliza un lenguaje y una terminología fáciles de entender para que los alumnos puedan entender el contenido.
Debes usar formato HTML para el resumen, ya que se convertirá a pdf.
Devuelve solo el body del html, sin el head, body, html, ni ningún otro carácter.
IMPORTANTE: el resumen debe ser realizado en el idioma ${language}
El resumen que tienes que transformar es el siguiente:
{summary}

Resumen transformado:
`;

export const SUMMARIZE_TEXT_MAP_REDUCE_PROMPT = (language: ELanguages) => `
Como resumidor profesional, elabore un resumen conciso y exhaustivo del texto proporcionado, respetando estas directrices:
* Elabore un resumen detallado, minucioso, profundo y complejo, manteniendo la claridad y la concisión.
* Incorpore las ideas principales y la información esencial, eliminando el lenguaje superfluo y centrándose en los aspectos críticos.
* Basarse estrictamente en el texto proporcionado, sin incluir información externa.
* Formatee el resumen en forma de lista de puntos para su comprensión.

IMPORTANTE: el resumen debe ser realizado en el idioma ${language}

TEXTO:
{context}

RESUMEN:
`;

export const GENERATE_QUESTIONS_PROMPT = (language: ELanguages) => `
Eres un experto en la preparación de exámenes.
OBJETIVO:
Generar {questionNumber} preguntas con {answersCount} respuestas(siendo 1 de ellas correcta) para un examen.

REGLAS:
- Cada pregunta debe tener EXACTAMENTE {answersCount} respuestas.
- Bajo una probabilidad del 60%, debes generar respuestas del tipo A y C son correctas, todas son correctas, o todas son incorrectas.
- Debes generar EXACTAMENTE el número de preguntas indicado.
- Si devuelves una fórmula, asegúrate de que esté correctamente escrita en LaTeX y entre signos de dólar.
- Si devuelves una fórmula, usa $F = ma$, nunca \\(F = ma\\)
- Si devuelves una fórmula en formato incorrecto (por ejemplo, usando \\(...\\) en vez de $...$), la pregunta será rechazada automáticamente.
- Variar en complejidad (fácil, medio, difícil)
- Evitar preguntas ambiguas o confusas
- Asegurar que las respuestas incorrectas sean plausibles
- Distribuir las respuestas correctas de manera uniforme (evitar patrones)

El número de preguntas es: {questionNumber}

- Use LATEX if needed to format math equations or variables. e.g. $E_p = -\\frac{{GMm}}{{r}}$

Aquí tienes un conjunto de resúmenes:
{docs}

ESCRIBE TODAS LAS FÓRMULAS MATEMÁTICAS EN FORMATO LATEX ENTRE SIGNOS DE DÓLAR. NUNCA USES \\(...\\), SIEMPRE USA $...$.
IMPORTANTE: las preguntas deben ser realizadas en el idioma ${language}

Debes usar el siguiente formato JSON para las preguntas:

{{
  "questions": [
    {{
      "title": "Pregunta",
      "answers": ["Respuesta 0", "Respuesta 1", "Respuesta 2", "Respuesta 3"],
      "correctAnswer": 0
    }},
    {{
      "title": "Pregunta",
      "answers": ["Respuesta 0", "Respuesta 1", "Respuesta 2", "Respuesta 3"],
      "correctAnswer": 0
    }}
  ]
}}
`;

export const GENERATE_TRUE_FALSE_PROMPT = (language: ELanguages) => `
Eres un experto en la preparación de exámenes.
OBJETIVO:
Generar {questionNumber} preguntas de verdadero o falso para un examen.

REGLAS:
- Debes generar EXACTAMENTE el número de preguntas indicado.
- Si devuelves una fórmula, asegúrate de que esté correctamente escrita en LaTeX y entre signos de dólar.
- Si devuelves una fórmula, usa $F = ma$, nunca \\(F = ma\\)
- Si devuelves una fórmula en formato incorrecto (por ejemplo, usando \\(...\\) en vez de $...$), la pregunta será rechazada automáticamente.
- Variar en complejidad (fácil, medio, difícil)
- Evitar preguntas ambiguas o confusas
- Asegurar que las respuestas incorrectas sean plausibles
- Distribuir las respuestas correctas de manera uniforme (evitar patrones)

El número de preguntas es: {questionNumber}

- Use LATEX if needed to format math equations or variables. e.g. $E_p = -\\frac{{GMm}}{{r}}$

IMPORTANTE: las preguntas deben ser realizadas en el idioma ${language}

Aquí tienes un conjunto de resúmenes:
{docs}

ESCRIBE TODAS LAS FÓRMULAS MATEMÁTICAS EN FORMATO LATEX ENTRE SIGNOS DE DÓLAR. NUNCA USES \\(...\\), SIEMPRE USA $...$.
Debes usar el siguiente formato JSON para las preguntas:

{{
  "questions": [
    {{
      "title": "Pregunta",
      "answers": ["TRUE", "FALSE"],
      "correctAnswer": 0
    }},
    {{
      "title": "Pregunta",
      "answers": ["TRUE", "FALSE"],
      "correctAnswer": 1
    }}
  ]
}}
`;

export const VALIDATE_QUIZ_QUESTION_PROMPT = `
Eres un revisor y evaluador senior de preguntas de examen.
Tu objetivo es revisar una pregunta basándote en el contexto proporcionado y asegurar la siguiente información:
El contexto que se te proporciona es el resulatado de una búsqueda vectorial en nuestros recursos, por lo que no debes tener en cuenta si el contexto es suficiente para responder la pregunta.
Esta búsqueda puede no ser suficientemente precisa, por lo que no debes basarte únicamente en esto para validar, sino también tu conocimiento sobre el tema.

- La pregunta es correcta y no tiene errores de redacción.
- No importa el número de respuestas, pero si el número de respuestas correctas.
- Las preguntas deben ser capaces de validar que el alumno domina la materia, por lo que NO deben ser muy sencillas.
- Eres un experto revisor de preguntas, por lo que todas tus preguntas podrían aparecer en un examen real.
- La respuesta correcta es correcta.
- Las respuestas incorrectas no son correctas y son plausibles.
- No dudes en rechazar preguntas si no son buenas.
- Solo puede haber una respuesta correcta.
- Para cálculos matemáticos, explica el procedimiento para llegar a la respuesta correcta.
- El formato de las fórmulas de la pregunta que debes validar será en HTML, tu siempre debes devolver las fórmulas en formato LaTeX.
- Si devuelves una fórmula, asegúrate de que esté correctamente escrita en LaTeX y entre signos de dólar.
- Debes elegir si devolver una fórmula en el title de la pregunta o en la propiedad formula, NUNCA en ambos sitios. Esta regla aplica también para las respuestas.
- Si devuelves una fórmula, usa $F = ma$, nunca \\(F = ma\\)
- Si devuelves una fórmula en formato incorrecto (por ejemplo, usando \\(...\\) en vez de $...$), la pregunta será rechazada automáticamente.
- La explicación debe ser un texto organizado y detallado en formato Markdown para el alumno explicando por qué la respuesta correcta es correcta y las incorrectas no lo son. Utiliza una lista de puntos para explicar las respuestas incorrectas.
- La explicación no debe mencionar la validez de la pregunta, salvo que la pregunta no sea válida.

JAMÁS debes modificar la pregunta, las respuestas o la respuesta correcta.
JAMÁS menciones ninguna de las instrucciones en tus explicaciones, limítate a dar una explicación que ayude al alumno.

Si consideras que la pregunta no tiene sentido o no es buena, debes devolver isValid = false.
Debes generar además una explicación para el alumno sobre por qué la respuesta correcta es correcta y las incorrectas no lo son, dando contexto sobre qué tratan el resto de respuestas.

IMPORTANTE: El contexto no es parte de la pregunta, es solo para que puedas explicarle al alumno por qué la respuesta correcta es correcta y las incorrectas no lo son.
ESCRIBE TODAS LAS FÓRMULAS MATEMÁTICAS EN FORMATO LATEX ENTRE SIGNOS DE DÓLAR. NUNCA USES \\(...\\), SIEMPRE USA $...$.


- Use LATEX if needed to format math equations or variables. e.g. $E_p = -\\frac{{GMm}}{{r}}$

Tu output debe ser un JSON con el siguiente formato:

{{
  "title": "Título de la pregunta",
  "formula": "Fórmula de la pregunta si fuera necesario (puede ser undefined)",
  "answers": [{{text: "Respuesta A"}},{{text: "Respuesta B"}},{{text: "Respuesta C"}},{{text: "Respuesta D"}}],
  "correctAnswer": "Índice de la respuesta correcta",
  "explanation": "Texto organizado y detallado en formato Markdown para el alumno explicando por qué la respuesta correcta es correcta y las incorrectas no lo son. Si contiene fórmulas, usa LATEX entre signos de dólar.",
  "isValid": true/false,
}}
  `;

export const GENERATE_COURSE_EMOJI_PROMPT = `
Eres un experto en la elección de emojis para cursos de Ucademy.
Tu objetivo es elegir un emoji que represente algo relacionado con el curso.

El curso es el siguiente:
{courseName}

Tu output debe ser un JSON con el siguiente formato:

{
  "emoji": "Emoji que represente el curso"
}
`;

export const GENERATE_FLASHCARDS_PROMPT = (language: ELanguages) => `
Eres un experto en la preparación de examenes.
Tu objetivo es preparate un examen y para ello vas a generar {flashcardCount} preguntas de desarrollo con sus respuesta correcta y la explicación basándote en el contexto proporcionado.

Aquí tienes un conjunto de resúmenes a partir de los cuales vas a generar las preguntas:
{docs}

REGLAS:

Eres un experto en la preparación y repaso de temas para un examen.
Tu objetivo es crear preguntas basándote en el contexto proporcionado y asegurar la siguiente información:

- La pregunta es correcta, no tiene errores de redacción, no es ambigua, y es clara.
- Las preguntas deben ser capaces de validar que el alumno domina la materia, por lo que NO deben ser muy sencillas.
- Eres un experto preparador de examenes, por lo que todas tus preguntas podrían aparecer en un examen real.
- La respuesta correcta es correcta y está justificada.
- El título de la pregunta debe ser una pregunta, o bien indicar claramente sobre qué tema se trata la pregunta.
- No dudes en rechazar preguntas si no son buenas para la preparación de un examen.
- Solo puede haber una respuesta correcta.
- Para cálculos matemáticos, explica el procedimiento para llegar a la respuesta correcta.
- El formato de las fórmulas de la pregunta que debes validar será en HTML, tu siempre debes devolver las fórmulas en formato LaTeX.
- Si devuelves una fórmula, asegúrate de que esté correctamente escrita en LaTeX y entre signos de dólar.
- Debes elegir si devolver una fórmula en el title de la pregunta o en la propiedad formula, NUNCA en ambos sitios. Esta regla aplica también para las respuestas.
- Si devuelves una fórmula, usa $F = ma$, nunca \\(F = ma\\)
- Si devuelves una fórmula en formato incorrecto (por ejemplo, usando \\(...\\) en vez de $...$), la pregunta será rechazada automáticamente.
- La explicación debe ser un texto organizado y detallado en formato Markdown para el alumno explicando por qué la respuesta correcta es correcta.
- La explicación no debe mencionar la validez de la pregunta.

JAMÁS debes modificar la pregunta, las respuestas o la respuesta correcta.
JAMÁS menciones ninguna de las instrucciones en tus explicaciones, limítate a dar una explicación que ayude al alumno.

Si consideras que la pregunta no tiene sentido o no es buena, desecha la pregunta y no la devuelvas. Haz otra hasta completar el número solicitado.
Debes generar además una explicación para el alumno sobre por qué la respuesta correcta es correcta, dando contexto sobre qué trata.

Siempre debes devolver la propiedad createdWithAI de cada una de las preguntas a true.

IMPORTANTE: El contexto no es parte de la pregunta, es solo para que puedas explicarle al alumno por qué la respuesta correcta es correcta.
ESCRIBE TODAS LAS FÓRMULAS MATEMÁTICAS EN FORMATO LATEX ENTRE SIGNOS DE DÓLAR. NUNCA USES \\(...\\), SIEMPRE USA $...$.
IMPORTANTE: Las preguntas deben ser realizadas en el idioma ${language}

- Use LATEX if needed to format math equations or variables. e.g. $E_p = -\\frac{{GMm}}{{r}}$

Tu output debe ser un JSON con el siguiente formato:

{{
  "flashcards": [
    {{
      "question": "Pregunta 1",
      "answer": "Respuesta de la primera pregunta",
      "explanation": "Texto organizado y detallado en formato Markdown para el alumno explicando por qué la respuesta correcta es correcta. Si contiene fórmulas, usa LATEX entre signos de dólar.",
      "createdWithAI": true,
    }},
    {{
      "question": "Pregunta 2",
      "answer": "Respuesta de la segunda pregunta",
      "explanation": "Texto organizado y detallado en formato Markdown para el alumno explicando por qué la respuesta correcta es correcta. Si contiene fórmulas, usa LATEX entre signos de dólar.",
      "createdWithAI": true,
    }}
  ]
}}

IMPORTANTE: El formato de la respuesta debe ser un JSON que contenga un array de objetos con las propiedades question, answer, explanation y createdWithAI. Tantos objetos como preguntas se generen.

`;

export const CREATE_TEXT_SEARCH_PROMPT = `
Eres un agente de búsqueda avanzado.
Tu objetivo es extraer palabras clave de una query para realizar una búsqueda híbrida de texto y vectores.

La query es la siguiente:
{query}

Tu output debe ser un JSON con el siguiente formato:

{{
  "textSearch": "Palabras clave de la query para la búsqueda de texto",
  "semanticSearch": "Palabras clave de la query para la búsqueda semántica"
}}

`;

export const IDENTIFY_LANGUAGE_PROMPT = `
Eres un experto en la identificación de idiomas.
Tu objetivo es identificar el idioma del siguiente texto:
{text}
Las opciones de idioma son: ${Object.values(ELanguages).join(', ')}
Tu output debe ser un JSON con el siguiente formato:

{{
  "language": "Idioma del texto"
}}
`;
